// Macro-Glossary — kurze deutsche Erklärung pro Wirtschafts-Indikator.
//
// Substring-Match weil Finnhub die Indicator-Namen leicht unterschiedlich
// liefert („CPI YoY" · „Consumer Price Index YoY" · „CPI MoM" · etc.).
// Reihenfolge der Patterns matters — spezifischere Treffer zuerst
// (z.B. „Core CPI" vor „CPI").

interface GlossaryEntry {
  pattern: RegExp;
  /** 1-2 Sätze auf Deutsch was der Indikator misst + Markt-Relevanz. */
  text: string;
}

const ENTRIES: ReadonlyArray<GlossaryEntry> = [
  // ── US Inflation ──
  {
    pattern: /core\s*cpi/i,
    text: 'Kerninflation (ohne Lebensmittel + Energie) — Fed-Lieblingsmaß. Über Erwartung = hawkisch (USD↑, Aktien↓).',
  },
  {
    pattern: /\bcpi\b/i,
    text: 'Verbraucherpreisindex — wichtigstes Inflations-Maß. Hohe Werte bedeuten Druck auf die Fed höhere Zinsen länger zu halten.',
  },
  {
    pattern: /\bppi\b/i,
    text: 'Erzeugerpreisindex — Inflation auf Produktionsebene. Leading-Indikator für CPI 1-3 Monate später.',
  },
  {
    pattern: /pce.*price|core\s*pce/i,
    text: 'Persönliche Konsumausgaben — bevorzugte Fed-Inflationsmessung über CPI. Core-PCE = ohne Food/Energy.',
  },
  // ── US Jobs ──
  {
    pattern: /non.?farm\s*payroll/i,
    text: 'Beschäftigung außerhalb Landwirtschaft — wichtigster Arbeitsmarkt-Datenpunkt. Stark = hawkisch.',
  },
  {
    pattern: /unemployment\s*rate/i,
    text: 'Arbeitslosenquote — Tief = Lohndruck, hawkisch für Fed. Sahm-Rule-Trigger bei >+0.5pp vs 12-Mo-Tief.',
  },
  {
    pattern: /initial\s*jobless\s*claims/i,
    text: 'Erstanträge Arbeitslosengeld (wöchentlich) — Hochfrequenz-Frühindikator für Arbeitsmarkt-Schwäche.',
  },
  {
    pattern: /continuing\s*claims/i,
    text: 'Bestand Arbeitslosengeld-Bezieher — zeigt wie schnell Entlassene wieder Job finden.',
  },
  {
    pattern: /average\s*hourly\s*earnings|wage\s*growth/i,
    text: 'Durchschnittliche Stundenlöhne — Lohninflations-Maß, Fed-relevant für Sticky-Inflation.',
  },
  {
    pattern: /jolts/i,
    text: 'Offene Stellen (JOLTS) — Maß für Arbeitsmarkt-Anspannung. Hoch = Lohndruck.',
  },
  {
    pattern: /adp/i,
    text: 'ADP-Beschäftigungsbericht (Privatsektor) — kommt 2 Tage vor NFP, mäßige Korrelation.',
  },
  // ── US Konsum + Industrie ──
  {
    pattern: /retail\s*sales/i,
    text: 'Einzelhandelsumsätze — wichtigster Konsum-Indikator (ca. 70 % BIP). Stark = bullisch für Aktien.',
  },
  {
    pattern: /consumer\s*confidence/i,
    text: 'Verbrauchervertrauen (Conference Board) — Forward-Looking-Konsum-Indikator.',
  },
  {
    pattern: /michigan|umich/i,
    text: 'Uni-Michigan-Stimmung — Inflations-Erwartungen-Komponente ist Fed-relevant.',
  },
  {
    pattern: /ism.*manufactur|manufact.*pmi/i,
    text: 'Einkaufsmanagerindex Industrie — >50 = Expansion, <50 = Kontraktion. ISM ist US-Standard.',
  },
  {
    pattern: /ism.*services|ism.*non.?manufactur|services.*pmi/i,
    text: 'Einkaufsmanagerindex Dienstleistungen — ca. 70 % der US-Wirtschaft, wichtiger als Manufacturing.',
  },
  {
    pattern: /industrial\s*production/i,
    text: 'Industrieproduktion — Volumen-Maß für Fertigung + Bergbau + Versorger.',
  },
  {
    pattern: /durable\s*goods/i,
    text: 'Aufträge langlebige Güter — Investitions-Frühindikator (Core ex Aircraft = stabilster Subindex).',
  },
  {
    pattern: /factory\s*orders/i,
    text: 'Auftragseingänge Fabriken — Industrie-Nachfrage-Indikator.',
  },
  // ── US Wohnen ──
  {
    pattern: /building\s*permit/i,
    text: 'Baugenehmigungen — Frühindikator für Housing-Aktivität 3-6 Monate später.',
  },
  {
    pattern: /housing\s*start/i,
    text: 'Baubeginne — Wohnbau-Konjunktur. Zins-sensitiv (steigt bei sinkenden Hypo-Sätzen).',
  },
  {
    pattern: /existing\s*home\s*sales/i,
    text: 'Verkäufe Bestands-Immobilien — Hypo-Sätze-getrieben, größter Wohnmarkt-Segment.',
  },
  {
    pattern: /new\s*home\s*sales/i,
    text: 'Neubau-Verkäufe — kleiner als Existing Home, aber BIP-relevant.',
  },
  {
    pattern: /case.?shiller|fhfa.*house/i,
    text: 'Hauspreis-Index — Inflations-Subkomponente (Owners-Equivalent-Rent).',
  },
  // ── US Makro + Fed ──
  {
    pattern: /\bgdp\b/i,
    text: 'Bruttoinlandsprodukt (BIP) — quartalsweise, Vorabschätzung (Advance) + Revisionen.',
  },
  {
    pattern: /fomc|fed.*decision|interest\s*rate.*decision/i,
    text: 'Fed-Zinsentscheidung — wichtigstes Macro-Event. Statement + Powell-Press-Conference bewegt Märkte stärker als der Zinsschritt selbst.',
  },
  {
    pattern: /fed.*funds.*rate/i,
    text: 'Fed Funds Rate — aktueller US-Leitzins.',
  },
  {
    pattern: /trade\s*balance/i,
    text: 'Handelsbilanz — Defizit ist USD-bearish auf lange Sicht, kurzfristig kaum Reaktion.',
  },
  {
    pattern: /current\s*account/i,
    text: 'Leistungsbilanz — strukturelle USD-Position, langfristiger FX-Treiber.',
  },
  // ── EU/DE ──
  {
    pattern: /ecb.*rate|euro.*rate.*decision/i,
    text: 'EZB-Zinsentscheidung — EUR-Volatilität-Treiber, Lagarde-Pressekonferenz folgt.',
  },
  {
    pattern: /hicp/i,
    text: 'Harmonisierter Verbraucherpreisindex EU — EZB-Inflationsmessung über EZB-Länder.',
  },
  {
    pattern: /\bifo\b/i,
    text: 'ifo-Geschäftsklimaindex DE — wichtigster deutscher Wirtschafts-Stimmungsindikator.',
  },
  {
    pattern: /\bzew\b/i,
    text: 'ZEW-Konjunkturerwartungen DE — Stimmung Finanzanalysten, leading vs ifo.',
  },
  {
    pattern: /german.*pmi|pmi.*germany/i,
    text: 'DE Einkaufsmanagerindex — wichtigster EU-Indikator (DE ≈ 30 % EU-BIP).',
  },
  {
    pattern: /(eu|euro)?.?zone.*pmi|pmi.*euro/i,
    text: 'Eurozone-Einkaufsmanagerindex — Composite/Manufacturing/Services, EUR-relevant.',
  },
  {
    pattern: /german.*cpi|cpi.*germany|deutsch.*verbraucher/i,
    text: 'DE Verbraucherpreisindex — Bellwether für EZB, EU-HICP folgt 1-2 Tage später.',
  },
  {
    pattern: /retail\s*sales.*german|german.*retail/i,
    text: 'DE Einzelhandelsumsätze — Konsum-Indikator größter EU-Volkswirtschaft.',
  },
  {
    pattern: /unemployment.*german|german.*unemploy/i,
    text: 'DE Arbeitslosenquote — strukturell niedrig, kleine Änderungen bewegen wenig.',
  },
  {
    pattern: /industrial.*production.*germ|german.*industrial/i,
    text: 'DE Industrieproduktion — DAX-relevant, Auto-/Maschinenbau-Schwergewicht.',
  },
];

/**
 * Findet die passende Glossar-Erklärung für einen Indicator-Namen.
 * Erste Treffer-Regel — spezifischere Patterns stehen oben in ENTRIES.
 */
export function getIndicatorExplanation(indicator: string): string | null {
  for (const entry of ENTRIES) {
    if (entry.pattern.test(indicator)) return entry.text;
  }
  return null;
}
